import { ApiService } from '../ApiService';
// Types importés localement si nécessaire
import type { 
  ExchangeRateHistory,
  UpdateExchangeRatesRequest,
  RevaluationRequest,
  RevaluationSummary,
  CurrencyRiskExposure
} from '../../../types/settings';
import type { CurrencyCode } from '../../../config/currency';

/**
 * Interface pour la liste des devises supportées
 */
export interface SupportedCurrency {
  code: string;
  name: string;
  symbol: string;
  isDefault: boolean;
  decimals: number;
}

/**
 * Paramètres de filtrage pour l'historique des taux
 */
export interface ExchangeRateHistoryParams {
  fromCurrency?: string;
  toCurrency?: string;
  startDate?: string;
  endDate?: string;
  source?: 'MANUAL' | 'IMPORT' | 'API' | 'SYSTEM';
  isActive?: boolean;
  page?: number;
  pageSize?: number;
}

/**
 * Paramètres pour l'analyse du risque de change
 */
export interface CurrencyRiskParams {
  fiscalYearId: string;
  asOfDate?: string;
  currencies?: string[];  // Si vide, analyse toutes les devises
}

/**
 * API pour la gestion des taux de change et devises
 */
export const exchangeRatesApi = {
  // ============================================
  // DEVISES SUPPORTÉES
  // ============================================

  /**
   * Récupérer la liste des devises supportées
   * @see GET /settings/currencies
   */
  getSupportedCurrencies: () =>
    ApiService.get<SupportedCurrency[]>('/settings/currencies'),

  // ============================================
  // TAUX DE CHANGE
  // ============================================

  /**
   * Récupérer les taux de change actuels
   * @see GET /settings/exchange-rates
   */
  getCurrentRates: () =>
    ApiService.get<Record<CurrencyCode, number>>('/settings/exchange-rates'),

  /**
   * Mettre à jour les taux de change
   * @see PUT /settings/exchange-rates
   */
  updateRates: (request: UpdateExchangeRatesRequest) =>
    ApiService.put<{ message: string }>('/settings/exchange-rates', request),

  /**
   * Ajouter un nouveau taux de change (avec historisation)
   * @see POST /settings/exchange-rates
   */
  addRate: (data: {
    fromCurrency: string;
    toCurrency: string;
    rate: number;
    effectiveDate?: string;
    notes?: string;
  }) =>
    ApiService.post<ExchangeRateHistory>('/settings/exchange-rates', data),

  /**
   * Récupérer l'historique des taux de change
   * @see GET /settings/exchange-rates/history
   */
  getHistory: (params?: ExchangeRateHistoryParams) =>
    ApiService.get<{
      data: ExchangeRateHistory[];
      total: number;
      page: number;
      pageSize: number;
    }>('/settings/exchange-rates/history', params),

  /**
   * Récupérer le taux de change pour une date spécifique
   * @see GET /settings/exchange-rates/:fromCurrency/:toCurrency
   */
  getRateForDate: (fromCurrency: string, toCurrency: string, date?: string) => {
    const params = date ? { date } : undefined;
    return ApiService.get<{
      rate: number;
      effectiveDate: string;
      source: string;
    }>(`/settings/exchange-rates/${fromCurrency}/${toCurrency}`, params);
  },

  // ============================================
  // RÉÉVALUATION DES DEVISES
  // ============================================

  /**
   * Lancer une réévaluation des positions en devises
   * @see POST /settings/exchange-rates/revaluation
   */
  runRevaluation: (request: RevaluationRequest) =>
    ApiService.post<RevaluationSummary>('/settings/exchange-rates/revaluation', request),

  /**
   * Récupérer l'historique des réévaluations
   * @see GET /settings/exchange-rates/revaluation/history
   */
  getRevaluationHistory: (params?: {
    fiscalYearId?: string;
    startDate?: string;
    endDate?: string;
    page?: number;
    pageSize?: number;
  }) =>
    ApiService.get<{
      data: RevaluationSummary[];
      total: number;
    }>('/settings/exchange-rates/revaluation/history', params),

  // ============================================
  // ANALYSE DU RISQUE DE CHANGE
  // ============================================

  /**
   * Récupérer l'exposition au risque de change
   * @see GET /settings/exchange-rates/risk-exposure
   */
  getRiskExposure: (params: CurrencyRiskParams) =>
    ApiService.get<{
      exposures: CurrencyRiskExposure[];
      totalRiskValue: number;
      baseCurrency: string;
      asOfDate: string;
    }>('/settings/exchange-rates/risk-exposure', params),

  // ============================================
  // UTILITAIRES
  // ============================================

  /**
   * Convertir un montant d'une devise vers une autre
   * Utilise les taux actuels du serveur
   */
  convertAmount: (amount: number, fromCurrency: string, toCurrency: string, date?: string) =>
    ApiService.post<{
      originalAmount: number;
      convertedAmount: number;
      rate: number;
      fromCurrency: string;
      toCurrency: string;
    }>('/settings/exchange-rates/convert', { amount, fromCurrency, toCurrency, date }),
};
